import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { AlertTriangle, Search, ThumbsDown, Calendar, Clock, HeartPulse } from 'lucide-react';
import { formatDateAU } from '../../utils/dateFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => `$${Math.round(v).toLocaleString()}`;

const getCurrentFYQuarter = () => {
  const now = new Date();
  const month = now.getMonth() + 1;
  if (month >= 7 && month <= 9) return 1;
  if (month >= 10 && month <= 12) return 2;
  if (month >= 1 && month <= 3) return 3;
  return 4;
};
const QUARTER_LABELS = { 1: 'Jul–Sep', 2: 'Oct–Dec', 3: 'Jan–Mar', 4: 'Apr–Jun' };

const getRiskReason = (plan) => {
  const reasons = [];
  if (plan.daysUntil < 0) reasons.push('Contract expired');
  else if (plan.daysUntil <= 30) reasons.push('Expiring within 30 days');
  else if (plan.daysUntil <= 60) reasons.push('Expiring within 60 days');
  if (plan.npsCategory === 'detractor') reasons.push('NPS Detractor');
  else if (plan.npsCategory === 'passive') reasons.push('NPS Passive');
  if (!plan.npsScore) reasons.push('No NPS data');
  return reasons[0] || 'Upcoming renewal';
};

const RiskTag = ({ reason }) => {
  const config = {
    'Contract expired': 'bg-red-100 text-red-700 border-red-200',
    'Expiring within 30 days': 'bg-red-50 text-red-600 border-red-200',
    'Expiring within 60 days': 'bg-amber-50 text-amber-700 border-amber-200',
    'NPS Detractor': 'bg-red-50 text-red-600 border-red-200',
    'NPS Passive': 'bg-amber-50 text-amber-600 border-amber-200',
    'No NPS data': 'bg-[#F5F5F5] text-[#737373] border-[#E5E5E5]',
    'Upcoming renewal': 'bg-yellow-50 text-yellow-700 border-yellow-200',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${config[reason] || config['Upcoming renewal']}`}>
      {reason}
    </span>
  );
};

const RiskRegister = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/revenue-security`);
      setData(res.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const { atRiskPlans, renewalFocusList, summary } = useMemo(() => {
    if (!data) return { atRiskPlans: [], renewalFocusList: [], summary: null };

    const { summary, plans } = data;
    const currentQ = getCurrentFYQuarter();
    const today = new Date();

    // At-risk: expired + expiring this quarter
    const thisQuarterPlans = plans.filter(p =>
      p.fy === summary.currentFY && p.quarter === currentQ && p.contractExpiry
    );
    const expiredPlans = plans.filter(p => p.status === 'unsecured');

    const combined = [...thisQuarterPlans, ...expiredPlans]
      .filter((p, i, arr) => arr.findIndex(x => x.planNumber === p.planNumber) === i)
      .map(p => {
        const expiryDate = new Date(p.contractExpiry);
        const daysUntil = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
        return { ...p, daysUntil };
      });

    // Prioritise by group revenue (highest group revenue = highest priority)
    const atRisk = combined.sort((a, b) => (b.groupRevenue || b.revenue) - (a.groupRevenue || a.revenue));

    // Renewal focus: next quarter's renewals (secured plans expiring in Q+1)
    const nextQ = currentQ < 4 ? currentQ + 1 : 1;
    const nextFY = currentQ < 4 ? summary.currentFY : summary.currentFY + 1;
    const renewalFocus = plans
      .filter(p => p.fy === nextFY && p.quarter === nextQ && p.status === 'secured')
      .sort((a, b) => (b.groupRevenue || b.revenue) - (a.groupRevenue || a.revenue));

    return { atRiskPlans: atRisk, renewalFocusList: renewalFocus, summary };
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-[#E5E5E5] border-t-[#171717] rounded-full animate-spin" />
      </div>
    );
  }

  if (!data || !summary) {
    return (
      <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-12 text-center">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-[#E5E5E5]" />
        <p className="text-sm text-[#525252]">No data available.</p>
      </div>
    );
  }

  const currentQ = getCurrentFYQuarter();
  const totalAtRisk = atRiskPlans.reduce((s, p) => s + p.revenue, 0);

  const filtered = atRiskPlans.filter(p => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return p.planNumber.toLowerCase().includes(t) || p.propertyName?.toLowerCase().includes(t) || p.manager?.toLowerCase().includes(t);
  });

  return (
    <div className="space-y-6" data-testid="risk-register-view">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-[#171717] font-heading">Risk Register</h1>
        <p className="text-sm text-[#737373] mt-1">
          FY{summary.currentFY} Q{currentQ} ({QUARTER_LABELS[currentQ]}) — Prioritised by group revenue
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-red-200 rounded-[10px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <p className="text-xs font-medium text-red-600 uppercase tracking-wider">Revenue at Risk</p>
          <p className="text-3xl font-bold text-red-600 mt-1">{fmt(totalAtRisk)}</p>
          <p className="text-xs text-red-400 mt-0.5">{atRiskPlans.length} plans</p>
        </div>
        <div className="bg-white border border-[#E5E5E5] rounded-[10px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <p className="text-xs font-medium text-[#737373] uppercase tracking-wider">Expired Contracts</p>
          <p className="text-3xl font-bold text-[#171717] mt-1">{atRiskPlans.filter(p => p.daysUntil < 0).length}</p>
          <p className="text-xs text-[#A3A3A3] mt-0.5">requiring immediate action</p>
        </div>
        <div className="bg-white border border-amber-200 rounded-[10px] p-5 shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <p className="text-xs font-medium text-amber-600 uppercase tracking-wider">Next Quarter Renewals</p>
          <p className="text-3xl font-bold text-amber-600 mt-1">{renewalFocusList.length}</p>
          <p className="text-xs text-amber-400 mt-0.5">{fmt(renewalFocusList.reduce((s, p) => s + p.revenue, 0))} revenue</p>
        </div>
      </div>

      {/* People Wellbeing — leading indicator sourced from Everperform pulse */}
      <PeopleWellbeingSection />

      {/* At-Risk Plans — Prioritised List */}
      <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
        <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-[8px] bg-red-50 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-red-500" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[#171717] font-heading">Plans at Risk</h2>
              <p className="text-xs text-[#737373]">Prioritised by total group revenue — highest value groups first</p>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A3A3A3]" strokeWidth={1.5} />
            <input
              data-testid="risk-search"
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm border border-[#E5E5E5] rounded-[8px] bg-[#F8F9FA] text-[#171717] placeholder-[#A3A3A3] focus:outline-none focus:border-[#171717] w-48"
            />
          </div>
        </div>
        <div className="overflow-x-auto max-h-[600px]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#F8F9FA] border-b border-[#E5E5E5]">
              <tr className="text-[10px] font-semibold uppercase tracking-wider text-[#737373]">
                <th className="px-4 py-2.5 text-center w-10">#</th>
                <th className="px-4 py-2.5 text-left">Plan</th>
                <th className="px-4 py-2.5 text-left">Property</th>
                <th className="px-4 py-2.5 text-left">Manager</th>
                <th className="px-3 py-2.5 text-right">Group Rev</th>
                <th className="px-3 py-2.5 text-right">Plan Rev</th>
                <th className="px-4 py-2.5 text-center">Expiry</th>
                <th className="px-4 py-2.5 text-center">NPS</th>
                <th className="px-4 py-2.5 text-left">Risk Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F5F5F5]">
              {filtered.map((plan, i) => (
                <tr key={plan.planNumber} className="hover:bg-[#F8F9FA]/50 transition-colors">
                  <td className="px-4 py-3 text-center text-xs font-bold text-[#A3A3A3]">{i + 1}</td>
                  <td className="px-4 py-3 font-mono text-xs font-medium text-[#171717]">{plan.planNumber}</td>
                  <td className="px-4 py-3 text-xs text-[#525252] truncate max-w-[140px]">{plan.propertyName || '-'}</td>
                  <td className="px-4 py-3 text-xs text-[#525252]">{plan.manager}</td>
                  <td className="px-3 py-3 text-right text-xs font-mono font-bold text-[#171717]">{fmt(plan.groupRevenue || plan.revenue)}</td>
                  <td className="px-3 py-3 text-right text-xs font-mono text-[#525252]">{fmt(plan.revenue)}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex flex-col items-center">
                      <span className="text-xs text-[#525252]">{formatDateAU(plan.contractExpiry)}</span>
                      <span className={`text-[10px] font-bold ${plan.daysUntil < 0 ? 'text-red-600' : plan.daysUntil <= 30 ? 'text-red-500' : 'text-amber-600'}`}>
                        {plan.daysUntil < 0 ? `${Math.abs(plan.daysUntil)}d overdue` : `${plan.daysUntil}d`}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {plan.npsScore != null ? (
                      <span className={`text-xs font-bold ${plan.npsCategory === 'promoter' ? 'text-emerald-600' : plan.npsCategory === 'detractor' ? 'text-red-600' : 'text-amber-600'}`}>
                        {plan.npsScore.toFixed(1)}
                      </span>
                    ) : (
                      <span className="text-[#A3A3A3] text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <RiskTag reason={getRiskReason(plan)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-3 border-t border-[#E5E5E5] bg-[#FAFAFA] rounded-b-[10px]">
          <p className="text-[10px] font-medium uppercase tracking-wider text-[#737373]">
            {filtered.length} plans — {fmt(filtered.reduce((s, p) => s + p.revenue, 0))} total revenue at risk
          </p>
        </div>
      </div>

      {/* Renewal Focus List — Next Quarter */}
      {renewalFocusList.length > 0 && (
        <div className="bg-white border border-amber-200 rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]">
          <div className="px-6 py-4 border-b border-amber-100 bg-amber-50/20 rounded-t-[10px]">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-[8px] bg-amber-100 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-amber-600" strokeWidth={1.5} />
              </div>
              <div>
                <h2 className="text-base font-semibold text-[#171717] font-heading">Renewal Focus — Next Quarter</h2>
                <p className="text-xs text-[#737373]">
                  {renewalFocusList.length} plans expiring next quarter — begin engagement now to secure renewals
                </p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto max-h-[400px]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#F8F9FA] border-b border-[#E5E5E5]">
                <tr className="text-[10px] font-semibold uppercase tracking-wider text-[#737373]">
                  <th className="px-4 py-2.5 text-center w-10">#</th>
                  <th className="px-4 py-2.5 text-left">Plan</th>
                  <th className="px-4 py-2.5 text-left">Property</th>
                  <th className="px-4 py-2.5 text-left">Manager</th>
                  <th className="px-3 py-2.5 text-right">Group Rev</th>
                  <th className="px-3 py-2.5 text-right">Plan Rev</th>
                  <th className="px-4 py-2.5 text-center">Expiry</th>
                  <th className="px-4 py-2.5 text-center">NPS</th>
                  <th className="px-4 py-2.5 text-left">Focus</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F5F5F5]">
                {renewalFocusList.map((plan, i) => (
                  <tr key={plan.planNumber} className="hover:bg-amber-50/30 transition-colors">
                    <td className="px-4 py-3 text-center text-xs font-bold text-amber-500">{i + 1}</td>
                    <td className="px-4 py-3 font-mono text-xs font-medium text-[#171717]">{plan.planNumber}</td>
                    <td className="px-4 py-3 text-xs text-[#525252] truncate max-w-[140px]">{plan.propertyName || '-'}</td>
                    <td className="px-4 py-3 text-xs text-[#525252]">{plan.manager}</td>
                    <td className="px-3 py-3 text-right text-xs font-mono font-bold text-[#171717]">{fmt(plan.groupRevenue || plan.revenue)}</td>
                    <td className="px-3 py-3 text-right text-xs font-mono text-[#525252]">{fmt(plan.revenue)}</td>
                    <td className="px-4 py-3 text-center text-xs text-[#525252]">{formatDateAU(plan.contractExpiry)}</td>
                    <td className="px-4 py-3 text-center">
                      {plan.npsScore != null ? (
                        <span className={`text-xs font-bold ${plan.npsCategory === 'promoter' ? 'text-emerald-600' : plan.npsCategory === 'detractor' ? 'text-red-600' : 'text-amber-600'}`}>
                          {plan.npsScore.toFixed(1)}
                        </span>
                      ) : (
                        <span className="text-[#A3A3A3] text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#525252]">
                      {plan.npsCategory === 'detractor' ? (
                        <span className="text-red-600 font-medium">Address NPS concerns before renewal</span>
                      ) : plan.npsCategory === 'passive' ? (
                        <span className="text-amber-600">Service review to improve satisfaction</span>
                      ) : plan.npsScore == null ? (
                        <span className="text-[#737373]">Send NPS survey before outreach</span>
                      ) : (
                        <span className="text-emerald-600">Proactive renewal — likely to sign</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-3 border-t border-amber-100 bg-amber-50/10 rounded-b-[10px]">
            <p className="text-[10px] font-medium uppercase tracking-wider text-[#737373]">
              {renewalFocusList.length} plans — {fmt(renewalFocusList.reduce((s, p) => s + p.revenue, 0))} revenue to secure next quarter
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskRegister;

// ── People Wellbeing — leading indicator from the Everperform pulse ─────────
// Anyone flagged as Critical or Watch is surfaced here so leaders can see
// wellbeing risks alongside plan / revenue risks in a single register.
const PEOPLE_BAND_STYLE = {
  critical: { chip: 'bg-rose-100 text-rose-800 border-rose-300',   dot: 'bg-rose-500' },
  watch:    { chip: 'bg-amber-100 text-amber-900 border-amber-300', dot: 'bg-amber-500' },
};
const PeopleWellbeingSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/care-watchlist`)
      .then(res => setData(res.data))
      .catch(() => setData({ counts: { critical: 0, watch: 0 }, peakAndFalling: [], watchlist: [] }))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return null;
  if (!data) return null;
  const rows = data.watchlist || [];
  if (rows.length === 0) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-[10px] px-4 py-3 text-sm text-emerald-800">
        <span className="font-semibold">People Wellbeing:</span> everyone&apos;s Everperform pulse is currently in the green.
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-[10px] shadow-[0_2px_10px_rgba(0,0,0,0.02)]" data-testid="risk-register-people">
      <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-[8px] bg-rose-50 flex items-center justify-center">
            <HeartPulse className="w-4 h-4 text-rose-600" strokeWidth={1.5} />
          </div>
          <div>
            <h3 className="text-base font-semibold text-[#171717]">People Wellbeing</h3>
            <p className="text-xs text-[#737373]">Leading indicator — sourced from the Everperform pulse feed. Check in before it surfaces as a plan issue.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 rounded-full text-[11px] font-bold border border-rose-300 bg-rose-50 text-rose-800">{data.counts?.critical || 0} Critical</span>
          <span className="px-2.5 py-1 rounded-full text-[11px] font-bold border border-amber-300 bg-amber-50 text-amber-900">{data.counts?.watch || 0} Watch</span>
          {data.peakAndFalling?.length > 0 && (
            <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-rose-600 text-white border border-rose-700">{data.peakAndFalling.length} Peak + falling</span>
          )}
        </div>
      </div>
      <div className="divide-y divide-[#F0F0F0]">
        {rows.slice(0, 12).map(r => {
          const s = PEOPLE_BAND_STYLE[r.band] || PEOPLE_BAND_STYLE.watch;
          return (
            <div key={r.name} className="px-6 py-3 flex items-center gap-4 flex-wrap">
              <div className="min-w-[220px] flex-1">
                <p className="text-sm font-semibold text-[#171717]">{r.name}</p>
                <p className="text-[11px] text-[#737373]">{r.title || 'Unknown role'}{r.teamLeader ? ` · ${r.teamLeader}` : ''}</p>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border whitespace-nowrap ${s.chip}`}>{r.label}</span>
              <div className="text-[11px] text-[#525252] min-w-[120px]">
                <span className="font-semibold text-[#171717]">{r.compositeScore == null ? '—' : Math.round(r.compositeScore)}</span> composite
                {r.compositeVariance != null && (
                  <span className={r.compositeVariance < 0 ? ' text-rose-700 font-semibold ml-1' : ' text-emerald-700 font-semibold ml-1'}>
                    ({r.compositeVariance > 0 ? '+' : ''}{Math.round(r.compositeVariance)})
                  </span>
                )}
              </div>
              <p className="text-[11px] text-[#737373] flex-1 min-w-[220px]">{r.triggers?.[0] || ''}</p>
            </div>
          );
        })}
        {rows.length > 12 && (
          <p className="px-6 py-2 text-[11px] text-[#A3A3A3] italic">+ {rows.length - 12} more on the Care Watchlist (see Dashboard).</p>
        )}
      </div>
    </div>
  );
};
